/**
 * WikiPageCreateModal — 채팅 메시지를 위키 페이지로 변환·저장
 *
 * 흐름:
 *   1. 모달 open + threadText 전달
 *   2. WikiPageAnalyzer (Gemini) 가 제목/개요/섹션/태그 생성
 *   3. 분석 결과로 prefill (제목·태그는 입력란, 본문은 TipTap JSON)
 *   4. 사용자가 부모 페이지 선택 + 본문 수정 가능
 *   5. 저장 → createPost (metadata.source_thread_id/source_room_id/ai_generated 포함)
 *   6. WikiPageEmitter.emit('created') + 토스트
 *
 * TaskCreateModal 패턴 미러.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Trans, t } from '@lingui/macro';
import {
  Loader2, Sparkles, BookOpen, Link2, Bell, FolderTree, ChevronRight,
  FolderOpen, FileText, Check, Pin, AtSign, Users,
} from 'lucide-react';
import { UserAvatar } from '../components/UserAvatar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../../components/ui/popover';
import { useToast } from '../../hooks/use-toast';
import {
  analyzeChatForWikiPage, buildWikiPageTipTapJson,
  WikiPageAnalysisResult,
} from '../../services/WikiPageAnalyzer';
import {
  createPost, fetchPageTree, notifyNewPost, uploadBoardImage,
} from '../../services/BoardService';
import { Board, Post, PostAuthor } from '../../models/Board';
import { My } from '../../services/My';
import { Fire } from '../../services/FireClient';
import { Message } from '../../models/Message';
import { WikiPageEmitter } from './WikiPageEmitter';
import BoardRichEditor from './BoardRichEditor';
import { MentionUser } from './MentionSuggestion';
import TagInput from './TagInput';
import { Checkbox } from '../../components/ui/checkbox';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 저장 대상 위키 게시판 */
  board: Board;
  /** 원본 채팅 메시지 텍스트 (쓰레드 포함 가능) */
  threadText: string;
  /** 원본 채팅방 ID */
  roomId: string;
  /** 원본 메시지 ID (출처 점프용) */
  threadMessageId?: string;
  /** 위키 출처 범위 — 'message'(해당 메시지만) | 'thread'(쓰레드 전체) */
  sourceScope?: 'message' | 'thread';
}

const WikiPageCreateModal: React.FC<Props> = ({
  open, onOpenChange, board, threadText, roomId, threadMessageId, sourceScope,
}) => {
  const { toast } = useToast();
  const [analyzing, setAnalyzing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [analysis, setAnalysis] = useState<WikiPageAnalysisResult | null>(null);

  // 편집 가능 필드
  const [title, setTitle] = useState('');
  const [editorJson, setEditorJson] = useState<any>(null);
  const [editorPlainText, setEditorPlainText] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [parentPostId, setParentPostId] = useState<string>(''); // '' = 루트
  /** 사이드바 고정 여부 (작성 시점에 설정, 최대 3개 검증은 서버 또는 후속) */
  const [isPinned, setIsPinned] = useState(false);
  // 채팅창에 위키 페이지 생성 알림 카드 보낼지 (기본 ON)
  const [notifyChat, setNotifyChat] = useState(true);

  // 부모 페이지 선택용 — 게시판 내 모든 페이지
  const [wikiPages, setWikiPages] = useState<Post[]>([]);

  /** 위키 페이지 inline tree picker (depth 들여쓰기) */
  const wikiPageOptions = useMemo(() => {
    const byParent = new Map<string | null, Post[]>();
    wikiPages.forEach(p => {
      const key = p.parent_post_id ?? null;
      const arr = byParent.get(key) ?? [];
      arr.push(p);
      byParent.set(key, arr);
    });
    byParent.forEach(group => group.sort((a, b) => a.title.localeCompare(b.title)));
    const out: Array<{ id: string; title: string; depth: number }> = [];
    const seen = new Set<string>();
    const visit = (parentId: string | null, depth: number) => {
      const list = byParent.get(parentId) ?? [];
      for (const p of list) {
        if (seen.has(p.id) || depth > 4) continue;
        seen.add(p.id);
        out.push({ id: p.id, title: p.title || t`Untitled`, depth });
        visit(p.id, depth + 1);
      }
    };
    visit(null, 0);
    return out;
  }, [wikiPages]);

  /** 출처 채팅 채널명 + 시각 (현재 시각으로 fallback) */
  const sourceMeta = useMemo(() => {
    const room = My.Rooms.get(roomId);
    const channelName = room?.name ?? t`Channel`;
    const now = new Date();
    const timeText = `Today ${now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
    return { channelName, timeText };
  }, [roomId]);

  // @멘션용 채널 멤버
  const mentionMembers = useMemo<MentionUser[]>(() => {
    const room = My.Rooms.get(roomId);
    if (!room) return [];
    return room.users
      .filter(u => u.id !== My.user?.id)
      .map(u => ({ id: u.id, name: u.name, profileUrl: u.photoUrl ?? null }));
  }, [roomId]);

  // 모달 열릴 때 AI 분석 + 페이지 트리 로드
  useEffect(() => {
    if (!open) return;
    setAnalyzing(true);
    setAnalysis(null);
    setTitle('');
    setEditorJson(null);
    setEditorPlainText('');
    setTags([]);
    setParentPostId('');
    setIsPinned(false);

    // 페이지 목록 로드 (부모 선택 + 위키링크 자동완성용)
    fetchPageTree(board.id).then(setWikiPages).catch(() => setWikiPages([]));

    // AI 분석
    const lang = (typeof navigator !== 'undefined' && navigator.language) || 'ko';
    let cancelled = false;
    analyzeChatForWikiPage(threadText, lang)
      .then(result => {
        if (cancelled) return;
        setAnalysis(result);
        setTitle(result.title);
        setTags(result.tags);
        const json = buildWikiPageTipTapJson(result);
        setEditorJson(json);
        // plain text는 summary + 각 섹션 텍스트로 대충 합성
        const plain = [
          result.summary,
          ...result.sections.flatMap(s => [s.heading, ...s.bullets]),
        ].filter(Boolean).join('\n');
        setEditorPlainText(plain);
      })
      .catch(err => {
        if (cancelled) return;
        console.warn('[WikiPageCreateModal] AI 분석 실패:', err);
        // AI 실패 시 빈 폼으로 fallback (사용자가 직접 작성)
        setTitle('');
        setEditorJson(null);
        setEditorPlainText(threadText.slice(0, 200));
      })
      .finally(() => {
        if (!cancelled) setAnalyzing(false);
      });

    return () => { cancelled = true; };
  }, [open, board.id, threadText]);

  const handleSubmit = async () => {
    if (!title.trim() || submitting || !My.user) return;
    setSubmitting(true);
    try {
      const author: PostAuthor = {
        uid: My.user.id,
        name: My.user.name,
        photoUrl: My.user.photoUrl ?? null,
      };
      const metadata: Record<string, any> = {
        source_thread_id: threadMessageId ?? null,
        source_scope: sourceScope ?? null,
        source_room_id: roomId,
        ai_generated: analysis !== null,
      };

      const created = await createPost({
        board_id: board.id,
        workspace_id: board.workspace_id,
        title: title.trim(),
        content: editorJson ?? undefined,
        plain_text: editorPlainText.trim() || undefined,
        author,
        tags: tags.length > 0 ? tags : undefined,
        is_pinned: isPinned,
        metadata,
        parent_post_id: parentPostId || null,
      });

      // 알림은 위키 채널 멤버에게 (실패해도 무시)
      notifyNewPost(
        board.workspace_id,
        board.id,
        board.name,
        title.trim(),
        created.id,
        [],
      );

      // 채팅에 AutoMessage 카드 삽입 — 사용자 선택 ON일 때만
      if (notifyChat) try {
        const room = My.Rooms.get(roomId);
        if (room && My.user) {
          const sysMsg = new Message(room, My.user);
          sysMsg.autoMessage = 'wiki-page-created';
          sysMsg.text = title.trim();
          sysMsg.autoMessageItemId = `${board.id}:${created.id}`;
          await Fire.msg.saveMessage(sysMsg);
        }
      } catch (err) {
        console.warn('[WikiPageCreateModal] AutoMessage 전송 실패:', err);
      }

      WikiPageEmitter.emit('created', roomId, board.id, created.id);
      toast({ title: t`Saved as wiki page` });
      onOpenChange(false);
    } catch (err) {
      console.error('[WikiPageCreateModal] 저장 실패:', err);
      toast({ title: t`Failed to save wiki page`, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[1400px] w-[96vw] max-h-[92vh] overflow-hidden p-0 flex flex-col gap-0">
        <DialogHeader className="px-5 py-3 border-b border-border shrink-0">
          <DialogTitle className="flex items-center gap-2 text-heading-sm">
            <BookOpen className="size-4 text-primary" />
            <Trans>Save as wiki page</Trans>
          </DialogTitle>
          <DialogDescription className="sr-only">
            <Trans>AI-generated draft from chat message. Edit title, content, and tags freely, then save.</Trans>
          </DialogDescription>
        </DialogHeader>

        {analyzing && !analysis ? (
          <div className="flex-1 flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            <span><Trans>AI is organizing into wiki page format...</Trans></span>
          </div>
        ) : (
          <div className="flex-1 min-h-0 flex flex-col md:flex-row overflow-hidden">
            {/* ── 좌측 본문 (에디터 풀폭) ── */}
            <div className="flex-1 min-w-0 overflow-y-auto px-6 py-5 space-y-4">
              {/* AI 정리 배너 — gradient → 단일 bg-primary/6 단순화 (Phase C 디자이너 권장) */}
              {analysis && (
                <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg bg-primary/6 border border-primary/20">
                  <Sparkles className="size-4 text-primary shrink-0 mt-0.5" />
                  <p className="flex-1 text-meta leading-relaxed text-foreground m-0">
                    <strong className="font-semibold"><Trans>AI-generated draft.</Trans></strong>
                    {' '}<Trans>Edit freely and post. Title, content, and tags are pre-filled.</Trans>
                  </p>
                </div>
              )}

              {/* 제목 — 큰 글씨 */}
              <Input
                placeholder={t`Page title`}
                value={title}
                onChange={e => setTitle(e.target.value)}
                className="h-auto text-display-lg font-bold tracking-tight border-0 px-0 py-1 focus-visible:ring-0 placeholder:text-muted-foreground/50"
              />

              {/* 작성자 메타 */}
              {My.user && (
                <div className="flex items-center gap-3 mt-1">
                  <UserAvatar src={My.user.photoUrl ?? null} name={My.user.name} className="size-8" />
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold text-foreground leading-none">{My.user.name}</span>
                    <span className="text-meta-sm text-muted-foreground mt-1"><Trans>Creation date is recorded when posted</Trans></span>
                  </div>
                </div>
              )}

              {/* Properties row */}
              <div className="flex items-center gap-1.5 flex-wrap pt-1">
                <div className="flex-1 min-w-[200px]">
                  <TagInput
                    value={tags}
                    onChange={setTags}
                    defaultTags={board.default_tags ?? []}
                    placeholder={t`Add tags...`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setIsPinned(v => !v)}
                  data-state={isPinned ? 'on' : undefined}
                  className="inline-flex items-center gap-1.5 h-[26px] px-2.5 rounded-full text-meta-sm font-medium border border-border text-muted-foreground hover:bg-muted/60 transition-colors data-[state=on]:bg-badge-caution-bg data-[state=on]:text-badge-caution data-[state=on]:border-transparent"
                >
                  <Pin className="size-3" />
                  {isPinned ? t`Pinned to sidebar` : t`Not pinned`}
                </button>
              </div>

              <hr className="border-border my-2" />

              {/* 본문 에디터 */}
              <BoardRichEditor
                initialContent={editorJson}
                onChange={(json, plainText) => {
                  setEditorJson(json);
                  setEditorPlainText(plainText);
                }}
                placeholder={t`Enter content... (@ to mention / wiki page link)`}
                minHeight={320}
                onImageUpload={async (file) => {
                  const userId = My.user?.id;
                  if (!userId) throw new Error('Login required');
                  return uploadBoardImage(file, userId, board.id);
                }}
                mentionMembers={mentionMembers}
                wikiPages={wikiPages.map(p => ({ kind: 'page' as const, id: p.id, title: p.title }))}
              />
            </div>

            {/* ── 우측 aside (부모 페이지 / 출처 / 알림 / 팁) ── */}
            <aside className="w-full md:w-[300px] shrink-0 border-t md:border-t-0 md:border-l border-border bg-muted/20 overflow-y-auto px-4 py-4 space-y-5">
              {/* 부모 페이지 — inline tree picker */}
              <div className="space-y-2">
                <div className="flex items-center gap-1.5 text-meta-sm font-semibold text-muted-foreground uppercase tracking-wider">
                  <FolderTree className="size-3" />
                  <span><Trans>Parent page</Trans></span>
                </div>
                <div className="rounded-lg border border-border bg-card p-1 space-y-px max-h-[220px] overflow-y-auto">
                  <button
                    type="button"
                    onClick={() => setParentPostId('')}
                    data-state={parentPostId === '' ? 'on' : undefined}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-meta text-muted-foreground hover:bg-muted/60 data-[state=on]:bg-primary/10 data-[state=on]:text-primary data-[state=on]:font-semibold transition-colors"
                  >
                    <FolderOpen className="size-3 shrink-0 opacity-70" />
                    <span className="flex-1 text-left truncate">— <Trans>Root page</Trans> —</span>
                    {parentPostId === '' && <Check className="size-3 shrink-0 text-primary" />}
                  </button>
                  {wikiPageOptions.map(opt => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setParentPostId(opt.id)}
                      data-state={parentPostId === opt.id ? 'on' : undefined}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-meta text-foreground hover:bg-muted/60 data-[state=on]:bg-primary/10 data-[state=on]:text-primary data-[state=on]:font-semibold transition-colors"
                      style={{ paddingLeft: opt.depth * 12 + 8 }}
                    >
                      <FileText className="size-3 shrink-0 opacity-70" />
                      <span className="flex-1 text-left truncate">{opt.title}</span>
                      {parentPostId === opt.id && <Check className="size-3 shrink-0 text-primary" />}
                    </button>
                  ))}
                </div>
              </div>

              {/* 출처 채팅 — channel + time + preview 분리 */}
              <div className="space-y-2">
                <div className="flex items-center gap-1.5 text-meta-sm font-semibold text-muted-foreground uppercase tracking-wider">
                  <Link2 className="size-3" />
                  <span><Trans>Source chat</Trans></span>
                </div>
                <div className="flex items-center gap-1.5 text-meta-sm text-muted-foreground">
                  <span className="font-medium text-foreground">#{sourceMeta.channelName}</span>
                  <span className="opacity-50">·</span>
                  <span>{sourceMeta.timeText}</span>
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="w-full text-left rounded-lg border border-border bg-card hover:bg-muted/50 p-2.5 text-meta-sm text-muted-foreground hover:text-foreground transition-colors space-y-1.5"
                      title={t`Preview original chat message`}
                    >
                      <div className="line-clamp-3 leading-snug text-foreground/80">
                        {threadText || t`(no message content)`}
                      </div>
                      <div className="flex items-center gap-1 text-meta-sm text-primary">
                        <span><Trans>Go to original message</Trans></span>
                        <ChevronRight className="size-3" />
                      </div>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    side="left"
                    align="start"
                    className="w-[480px] max-h-[60vh] overflow-y-auto p-0"
                  >
                    <div className="px-3 py-2 border-b border-border flex items-center gap-1.5 text-meta-sm font-semibold text-muted-foreground uppercase tracking-wider">
                      <Link2 className="size-3" />
                      <span><Trans>Original chat message</Trans></span>
                    </div>
                    <div className="px-3 py-2.5 text-label leading-relaxed text-foreground whitespace-pre-wrap break-words">
                      {threadText || t`(no message content)`}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* 알림 — 박스 풀폭 */}
              <div className="space-y-2">
                <div className="flex items-center gap-1.5 text-meta-sm font-semibold text-muted-foreground uppercase tracking-wider">
                  <Bell className="size-3" />
                  <span><Trans>Notifications</Trans></span>
                </div>
                <label
                  className="w-full flex items-start gap-2 px-3 py-2.5 rounded-lg border border-border bg-card hover:bg-muted/50 cursor-pointer select-none transition-colors"
                >
                  <Checkbox
                    checked={notifyChat}
                    onCheckedChange={v => setNotifyChat(v === true)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-meta font-semibold text-foreground"><Trans>Send notification to chat</Trans></div>
                    <div className="text-meta-sm text-muted-foreground mt-0.5 leading-snug">
                      <Trans>Sends a wiki page created card to #{sourceMeta.channelName} channel</Trans>
                    </div>
                  </div>
                </label>
              </div>

              {/* 작성 팁 */}
              <div className="space-y-2">
                <div className="flex items-center gap-1.5 text-meta-sm font-semibold text-muted-foreground uppercase tracking-wider">
                  <BookOpen className="size-3" />
                  <span><Trans>Writing tips</Trans></span>
                </div>
                <div className="text-meta-sm text-muted-foreground leading-relaxed space-y-1.5">
                  <p className="m-0">
                    <Trans>Use <strong>@</strong> to mention members, <strong>[[</strong> to link other wiki pages.</Trans>
                  </p>
                  <p className="m-0"><Trans>Post AI-generated draft as-is or edit freely.</Trans></p>
                </div>
              </div>
            </aside>
          </div>
        )}

        {/* ── 푸터 액션 ── */}
        {!(analyzing && !analysis) && (
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border shrink-0 bg-background">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              <Trans>Cancel</Trans>
            </Button>
            <Button onClick={handleSubmit} disabled={!title.trim() || submitting}>
              {submitting ? t`Saving...` : t`Save`}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WikiPageCreateModal;
